'use client';
/* eslint-disable @next/next/no-img-element -- collections UI: <img> covers; prototype store, not backend */ // ---------------------------------------------------------------------------
// /library — browse and manage a library, no project required.
//
// You are always in exactly ONE library at a time — yours, or a team's — and
// the URL says which: /library is personal, /team/<slug>/library is that
// team's (this page serves both routes, same pattern as /projects). The
// navbar chip mirrors the URL; deep-linking a collection uses ?c=<id>.
//
// Left rail = the SAME browse component as the in-editor panel, scoped to the
// current owner. Right = the cast wall, or the SAME inspector embedded at
// full width. One entity, three zoom levels; ownership is a first-class,
// always-visible context — never a mixed pile.
//
// Backed by the persistent library store (IndexedDB): this is the user's real
// library — their uploads survive reloads. Drop images or video anywhere on
// the wall to add them.
// ---------------------------------------------------------------------------
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type DragEvent } from 'react';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useMemberships } from '@/hooks/useMemberships';
import { useWorkspaceRouteRestore } from '@/hooks/useWorkspaceRouteRestore';
import { Plus } from '@/components/ui/icons';
import { Button } from '@/components/ui/button';
import { LibraryGhostModeBanner } from '@/components/LibraryGhostModeBanner';
import CollectionInspector from '@/components/collections/CollectionInspector';
import CollectionsLibrarySection, { LibraryConceptDiagram } from '@/components/collections/CollectionsLibrarySection';
import { LibraryAccessDeniedNotice } from '@/components/collections/LibraryAccessDeniedNotice';
import { LibraryLoadFailedNotice } from '@/components/collections/LibraryLoadFailedNotice';
import { CommunityLibraryDialog } from '@/components/collections/CommunityLibraryDialog';
import { SAMPLE_LIBRARY_AVAILABLE } from '@/components/collections/sampleLibrary';
import { getAssetUrl, heroAsset, useLibraryStore, type LibAsset, type LibCollection } from '@/store/libraryStore';
import { useWorkspaceContext } from '@/store/workspaceContextStore';
import { useLibraryGhostMode } from '@/store/ghostModeStore';
import { explicitWorkspaceHref, parseWorkspaceRoute } from '@/lib/workspaceRoutes';
function WallCard({ c, assets, onOpen }: {
    c: LibCollection;
    assets: LibAsset[];
    onOpen: () => void;
}) {
    const hero = heroAsset(assets, c);
    const cover = hero ? getAssetUrl(hero) : null;
    const count = assets.filter((a)=>a.collectionId === c.id).length;
    return (<button onClick={onOpen} className="group text-left">
      <div className="relative aspect-[4/3] overflow-hidden rounded-lg border border-border/70 bg-muted/30 transition duration-150 group-hover:border-foreground/25 group-hover:shadow-lg group-hover:shadow-black/30">
        {cover && (<img src={cover} alt="" loading="lazy" className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-[1.02]"/>)}
      </div>
      <div className="mt-2 flex h-5 items-center gap-1.5">
        <span className="min-w-0 truncate text-sm leading-none text-foreground/80 transition-colors group-hover:text-foreground">
          {c.name}
        </span>
        <span className="ml-auto shrink-0 text-[11px] tabular-nums text-muted-foreground/50">{count}</span>
      </div>
    </button>);
}
// Live library variables — linked prompt snippets, one source of truth across
// projects. Minimal and quiet: click a name/value to edit, ⌘Z to undo a delete.
export default function LibraryPage() {
    const t = useTranslations$1();
    const hydrated = useLibraryStore((s)=>s.hydrated);
    const accessDenied = useLibraryStore((s)=>s.accessDenied);
    const accessDeniedReason = useLibraryStore((s)=>s.accessDeniedReason);
    const collections = useLibraryStore((s)=>s.collections);
    const assets = useLibraryStore((s)=>s.assets);
    const importFiles = useLibraryStore((s)=>s.importFiles);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [communityOpen, setCommunityOpen] = useState(false);
    const [dragging, setDragging] = useState(false);
    // Count of files in an in-flight import — drives the drop-overlay busy state.
    const [importing, setImporting] = useState(0);
    // Bumping this asks the browse rail to open its New-collection form.
    const [createReq, setCreateReq] = useState(0);
    const fileRef = useRef<HTMLInputElement>(null);
    // Each workspace owns ONE library. Explicit Personal/team paths name it;
    // bare /library is an app-entry route that restores the workspace you left.
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const { memberships, isLoading: membershipsLoading } = useMemberships();
    const libraryGhostSession = useLibraryGhostMode((s)=>s.session);
    const isLibraryGhosting = !!libraryGhostSession;
    const isGhostReadOnly = isLibraryGhosting && libraryGhostSession.mode !== 'write';
    const workspaceRoute = useMemo(()=>parseWorkspaceRoute(pathname), [
        pathname
    ]);
    const urlTeamSlug = workspaceRoute?.destination.kind === 'team' ? workspaceRoute.destination.slug : null;
    const explicitPersonal = workspaceRoute?.destination.kind === 'personal';
    const activeTeam = useMemo(()=>(urlTeamSlug ? (memberships?.teamOrgs?.find((t)=>t.slug === urlTeamSlug) ?? null) : null), [
        memberships?.teamOrgs,
        urlTeamSlug
    ]);
    const resolvingWorkspace = !isLibraryGhosting && !!urlTeamSlug && !activeTeam;
    // …and the other direction: a bare /library adopts the workspace you left
    // off in, instead of showing personal under a team-named chip (ENG-725).
    // A bare `/library?c=<id>` is a shared link to a PERSONAL collection — a team
    // collection's link carries its slug — so it names a destination rather than
    // entering the app, and restoring a workspace over it would break the link.
    const collectionDeepLink = !!searchParams.get('c');
    const { restoring: restoringWorkspace, holdContextSync } = useWorkspaceRouteRestore('library', workspaceRoute?.destination.kind === 'remembered' && !isLibraryGhosting && !collectionDeepLink);
    const contextId = libraryGhostSession?.targetOrgId ?? activeTeam?.id ?? 'personal';
    useEffect(()=>{
        if (resolvingWorkspace || restoringWorkspace) return;
        // Which library's DATA to load always follows the URL — even when the
        // remembered workspace couldn't be checked, personal is what's rendering.
        void useLibraryStore.getState().setWorkspace(contextId);
    }, [
        contextId,
        resolvingWorkspace,
        restoringWorkspace
    ]);
    useEffect(()=>{
        if (resolvingWorkspace || holdContextSync) return;
        if (explicitPersonal) {
            useWorkspaceContext.getState().setContext('personal');
            return;
        }
        if (useWorkspaceContext.getState().contextId !== contextId) {
            useWorkspaceContext.getState().setContext(contextId);
        }
    }, [
        contextId,
        explicitPersonal,
        resolvingWorkspace,
        holdContextSync
    ]);
    // A team slug you're not a member of (or a stale link) bounces to personal.
    useEffect(()=>{
        if (!isLibraryGhosting && urlTeamSlug && !membershipsLoading && !activeTeam) {
            router.replace(explicitWorkspaceHref({
                kind: 'personal'
            }, 'library'));
        }
    }, [
        isLibraryGhosting,
        urlTeamSlug,
        membershipsLoading,
        activeTeam,
        router
    ]);
    // The open collection can stop existing under us (deleted from the rail,
    // or its create was undone). Close the inspector instead of showing a ghost.
    useEffect(()=>{
        if (!hydrated || !selectedId) return;
        // eslint-disable-next-line react-hooks/set-state-in-effect -- reacting to the selected row being deleted out from under the inspector (external store event, not render state).
        if (!collections.some((c)=>c.id === selectedId && !c.deletedAt)) setSelectedId(null);
    }, [
        hydrated,
        selectedId,
        collections
    ]);
    // Workspace switched → drop the open inspector (it belongs to the other library).
    const [ctxSeen, setCtxSeen] = useState(contextId);
    if (contextId !== ctxSeen) {
        setCtxSeen(contextId);
        setSelectedId(null);
    }
    // ?c=<id> deep-links a collection; selection writes back shallowly so the
    // URL is always shareable (ids are stable forever — safe to link).
    const deepLinked = useRef(false);
    const storeWorkspaceId = useLibraryStore((st)=>st.workspaceId);
    useEffect(()=>{
        // Consume ?c= only once the STORE holds the URL's workspace — on a cold
        // /team/<slug>/library?c= load an interim personal hydration would
        // otherwise eat (and lose) the deep link before memberships resolve.
        if (deepLinked.current || !hydrated || resolvingWorkspace || restoringWorkspace || storeWorkspaceId !== contextId) return;
        deepLinked.current = true;
        const c = searchParams.get('c');
        // eslint-disable-next-line react-hooks/set-state-in-effect -- deep-link: open the ?c= collection once route state is ready.
        if (c && collections.some((col)=>col.id === c && !col.deletedAt)) setSelectedId(c);
    }, [
        hydrated,
        resolvingWorkspace,
        restoringWorkspace,
        storeWorkspaceId,
        contextId,
        searchParams,
        collections
    ]);
    useEffect(()=>{
        if (!deepLinked.current) return;
        const url = new URL(window.location.href);
        if (selectedId) url.searchParams.set('c', selectedId);
        else url.searchParams.delete('c');
        window.history.replaceState(null, '', url);
    }, [
        selectedId
    ]);
    const wall = collections.filter((c)=>!c.deletedAt);
    const liveAssets = assets.filter((a)=>!a.deletedAt);
    const importAndSelect = (files: File[])=>{
        if (isGhostReadOnly) return;
        if (!files.length) return;
        setImporting(files.length);
        // Inspector open → uploads land in the open collection; otherwise
        // importFiles spins up an 'Uploads' collection and we jump into it.
        void importFiles(files, selectedId).then((id)=>{
            if (id && !selectedId) setSelectedId(id);
        }).finally(()=>setImporting(0));
    };
    const onPick = (e: ChangeEvent<HTMLInputElement>)=>{
        const files = Array.from(e.target.files ?? []);
        e.target.value = ''; // let the same files be re-picked
        importAndSelect(files);
    };
    const onDragOver = (e: DragEvent<HTMLDivElement>)=>{
        if (isGhostReadOnly) return;
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setDragging(true);
    };
    const onDragLeave = (e: DragEvent<HTMLDivElement>)=>{
        if (e.currentTarget.contains(e.relatedTarget as Node)) return; // ignore child flicker
        setDragging(false);
    };
    const onDrop = (e: DragEvent<HTMLDivElement>)=>{
        if (isGhostReadOnly) return;
        if (!e.dataTransfer.types.includes('Files')) return;
        e.preventDefault();
        setDragging(false);
        importAndSelect(Array.from(e.dataTransfer.files));
    };
    return (<div className="flex min-h-0 flex-1 flex-col">
      {libraryGhostSession && <LibraryGhostModeBanner session={libraryGhostSession}/>}
      <input ref={fileRef} type="file" multiple accept="image/*,video/*" className="hidden" onChange={onPick}/>

      <div className="flex min-h-0 flex-1 gap-3 p-3">
        { /* Browse rail — the same component as the in-editor panel, floated
            like a canvas panel so /library reads as the editor's universe. */ }
        <div className="flex w-[340px] shrink-0 flex-col overflow-hidden rounded-lg border border-border bg-card shadow-2xl">
          <CollectionsLibrarySection key={contextId} selectedId={selectedId} onSelect={setSelectedId} libraryPage createRequestId={createReq}/>
        </div>

        { /* Content: cast wall on the open surface, or the embedded inspector
            as its own floating card. Page actions float over the surface,
            canvas-style — no chrome row. */ }
        <div className="relative flex min-w-0 flex-1 flex-col">
          { /* A failed snapshot fetch leaves stale-or-empty rows. Say so above
              the wall so an unreachable library never reads as an empty one. */ }
          <LibraryLoadFailedNotice className="mb-3 shrink-0"/>
          {!isGhostReadOnly && !selectedId && hydrated && wall.length > 0 && (<div className="flex shrink-0 items-center justify-end gap-2 pb-3">
              <Button size="sm" variant="outline" onClick={()=>fileRef.current?.click()}>
                {t("0t8z1W")}
              </Button>
              <Button size="sm" onClick={()=>setCreateReq((n)=>n + 1)}>
                <Plus className="size-4"/>
                {t("36dp_F")}
              </Button>
            </div>)}
          {accessDenied ? (<LibraryAccessDeniedNotice reason={accessDeniedReason} className="min-h-0 flex-1"/>) : selectedId ? (/* The page is the surface (same as the home wall): the collection
               view renders directly on it; only the rail and the asset detail
               panel are docked cards (the canvas grammar). */ <div className="min-h-0 flex-1 overflow-hidden">
              <CollectionInspector key={selectedId} id={selectedId} embedded surface="page" readOnly={isGhostReadOnly} onClose={()=>setSelectedId(null)}/>
            </div>) : !hydrated ? null : (<>
              <div className="min-h-0 flex-1 overflow-auto px-6 pb-6" onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop}>
                {wall.length === 0 ? (// Empty wall = the explainer surface: draw the shape of the
    // library, then offer the three ways in (create, import, drop).
    <div className="flex h-full flex-col items-center justify-center gap-5 text-center">
                    <div className="w-full max-w-sm">
                      <LibraryConceptDiagram wide/>
                    </div>
                    <p className="max-w-md text-sm leading-relaxed text-muted-foreground">
                      {t("XDW7rr")}{' '}
                      <code className="rounded bg-muted px-1 text-foreground">@Sarah</code>{' '}
                      {t("847Ba0")}
                    </p>
                    {!isGhostReadOnly && (<div className="flex items-center gap-2">
                        <Button size="sm" onClick={()=>setCreateReq((n)=>n + 1)}>
                          <Plus className="size-4"/>
                          {t("36dp_F")}
                        </Button>
                        {SAMPLE_LIBRARY_AVAILABLE && (<Button size="sm" variant="outline" onClick={()=>setCommunityOpen(true)}>
                            {t("fuD1Zd")}
                          </Button>)}
                      </div>)}
                    {!isGhostReadOnly && (<p className="text-xs text-muted-foreground/60">
                        {t("QR88lj")}
                      </p>)}
                  </div>) : (<>
                    <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
                      {wall.map((c)=>(<WallCard key={c.id} c={c} assets={liveAssets} onOpen={()=>setSelectedId(c.id)}/>))}
                    </div>
                  </>)}
              </div>
              {(dragging || importing > 0) && (<div className="pointer-events-none absolute inset-2 z-10 flex items-center justify-center rounded-lg border-2 border-dashed border-foreground/40 bg-background/70 backdrop-blur-[2px]">
                  <span className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-foreground shadow-md">
                    {importing > 0 ? t("AOGhUs", {
        count: String(importing),
        plural: importing === 1 ? '' : 's'
    }) : t("hsm_69")}
                  </span>
                </div>)}
            </>)}
        </div>
      </div>
      <CommunityLibraryDialog open={communityOpen} onOpenChange={setCommunityOpen}/>
    </div>);
}
